import type { AbsenceDate } from './valueObjects/absenceDate';
import type { AbsenceReason } from './valueObjects/absenceReason';
import type { AbsenceReasonDetailInfection } from './valueObjects/absenceReasonDetailInfection';
import type { AbsenceReasonDetailSick } from './valueObjects/absenceReasonDetailSick';
import type { ChildId } from './valueObjects/childId';
import type { Memo } from './valueObjects/memo';
import type { NurseryId } from './valueObjects/nurseryId';
import type { ParentId } from './valueObjects/parentId';
import type { AttendContactAbsenceJsonPub, AttendContactAbsenceJsonPvt } from '@/types/domain/attendContact/entities/attendContactAbsenceEntity';

import {
  EntityPub,
} from '@/domain/entityPub';
import { DeleteType } from '@/domain/entityRepositoryBase';
import {
  EntityName,
} from '@/infra/appsync';

/**
 * 欠席連絡プロパティ非公開
 */
export type AttendContactAbsencePropsPvt = {
  /** 施設ID */
  nurseryId: NurseryId,
  /** 親ID */
  parentId: ParentId,
  /** 子ID */
  childId: ChildId,
  /** 欠席日 */
  absenceDate: AbsenceDate,
  /** 欠席理由 */
  absenceReason: AbsenceReason,
  /** 欠席理由詳細病気 */
  absenceReasonDetailSick?: AbsenceReasonDetailSick,
  /** 欠席理由詳細感染症 */
  absenceReasonDetailInfection?: AbsenceReasonDetailInfection,
  /** メモ */
  memo: Memo,
}

/**
 * 欠席連絡プロパティ公開
 */
export type AttendContactAbsencePropsPub = Record<string, never>

/**
 * 欠席プロパティ
 */
export type AbsenceProps = {
  /** 理由 */
  reason: AbsenceReason,
  /** 理由詳細病気 */
  reasonDetailSick?: AbsenceReasonDetailSick,
  /** 理由詳細感染症 */
  reasonDetailInfection?: AbsenceReasonDetailInfection,
}

/**
 * 欠席連絡Entity
 */
export class AttendContactAbsenceEntity extends EntityPub<
  AttendContactAbsencePropsPvt,
  AttendContactAbsenceJsonPvt,
  AttendContactAbsencePropsPub,
  AttendContactAbsenceJsonPub
> {
  get deleteType(): DeleteType {
    return DeleteType.Dt;
  }

  get deleteKey(): string {
    return this.getFutureYear(this.absenceDate.value, 1).toJSON();
  }

  toJsonPubData(): AttendContactAbsenceJsonPub {
    return {};
  }

  toJsonData(): AttendContactAbsenceJsonPvt {
    const p: AttendContactAbsenceJsonPvt = {
      nurseryId: this.nurseryId.value,
      parentId: this.parentId.value,
      childId: this.childId.value,
      absenceDate: this.absenceDate.toStringDate(),
      absenceReason: this.absence.reason.value,
      absenceReasonDetailSick: this.absence.reasonDetailSick?.value,
      absenceReasonDetailInfection: this.absence.reasonDetailInfection?.value,
      memo: this.memo.value,
    };
    return p;
  }

  /**
   * Entity名
   */
  get entityName(): EntityName {
    const name: EntityName = EntityName.WagakoNurseryAttendContactAttendContactAbsenceEntity;
    return name;
  }

  /**
   * Entityキー
   */
  get entityKey(): string {
    const ymd = this.absenceDate.toStringDate();
    return `${ymd}#${this.parentId.value}_${this.childId.value}#${this.nurseryId.value}`;
  }

  /** サブキー */
  get subKey(): string | null {
    const ymd = this.absenceDate.toStringDate();
    return `${this.parentId.value}_${this.childId.value}#${ymd}#${this.nurseryId.value}`;
  }

  /** 施設ID */
  get nurseryId(): NurseryId {
    return this._value.nurseryId;
  }

  /** 施設ID */
  set nurseryId(nurseryId: NurseryId) {
    this._value.nurseryId = nurseryId;
  }

  /** 親ID */
  get parentId(): ParentId {
    return this._value.parentId;
  }

  /** 親ID */
  set parentId(parentId: ParentId) {
    this._value.parentId = parentId;
  }

  /** 子ID */
  get childId(): ChildId {
    return this._value.childId;
  }

  /** 子ID */
  set childId(childId: ChildId) {
    this._value.childId = childId;
  }

  /** 欠席日 */
  get absenceDate(): AbsenceDate {
    return this._value.absenceDate;
  }

  /** 欠席日 */
  set absenceDate(absenceDate: AbsenceDate) {
    this._value.absenceDate = absenceDate;
  }

  /**
   * 欠席
   */
  get absence(): AbsenceProps {
    const absence: AbsenceProps = {
      reason: this._value.absenceReason,
    };
    if (absence.reason.value === '病欠') {
      absence.reasonDetailSick = this._value.absenceReasonDetailSick;
    } else if (absence.reason.value === '感染症') {
      absence.reasonDetailInfection = this._value.absenceReasonDetailInfection;
    }
    return absence;
  }

  /**
   * 欠席
   */
  set absence(absence: AbsenceProps) {
    this._value.absenceReason = absence.reason;
    if (absence.reason.value === '病欠') {
      this._value.absenceReasonDetailSick = absence.reasonDetailSick;
    } else if (absence.reason.value === '感染症') {
      this._value.absenceReasonDetailInfection = absence.reasonDetailInfection;
    }
  }

  /** メモ */
  get memo(): Memo {
    return this._value.memo;
  }

  /** メモ */
  set memo(memo: Memo) {
    this._value.memo = memo;
  }
}
